import {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// MUI
import Box           from '@mui/material/Box';
import Typography    from '@mui/material/Typography';
import Grid          from '@mui/material/Grid';
import TextField     from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip          from '@mui/material/Chip';
import Button        from '@mui/material/Button';
import Fade          from '@mui/material/Fade';
import Skeleton      from '@mui/material/Skeleton';
import Alert         from '@mui/material/Alert';
import Snackbar      from '@mui/material/Snackbar';

import SearchIcon    from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ImageIcon     from '@mui/icons-material/Image';
import WhatsAppIcon  from '@mui/icons-material/WhatsApp';
import ShareIcon     from '@mui/icons-material/Share';

// Local
import { getCatalog } from './catalogApi';
import { CatalogGridSkeleton } from './CatalogSkeletons';
import CatalogProductoDetalle from './CatalogProductoDetalle';
import { copyCatalogLink } from './catalogShare';
import CatalogImageGallery from './CatalogImageGallery';
import { getProductImageUrls } from '../retail-pos/utils/productImages';
import CatalogStockLabel from './CatalogStockLabel';
import CatalogInterestStar from './CatalogInterestStar';
import CatalogInterestModal from './CatalogInterestModal';
import CatalogWhatsAppModal from './CatalogWhatsAppModal';
import { useCatalogInterests } from './useCatalogInterests';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 350;
const ITEMS_PER_PAGE = 24;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProductCard({
  producto,
  moneda,
  onClick,
  showImages = true,
  showCodigoBarras = false,
  interestActive,
  onInterestClick,
  interestJustSaved,
}) {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // IntersectionObserver for slide-in animation
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={cardRef}
      onClick={() => onClick(producto)}
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 4px rgba(0,0,0,.07)',
        cursor: 'pointer',
        transition: 'box-shadow .18s ease, opacity .4s ease',
        opacity: visible ? 1 : 0,
        '&:hover': {
          boxShadow: '0 6px 16px rgba(0,0,0,.12)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CatalogImageGallery
          urls={showImages ? getProductImageUrls(producto) : []}
          alt={producto.nombre}
          showImages={showImages}
        />
        <CatalogInterestStar
          active={interestActive}
          onClick={onInterestClick}
          justSaved={interestJustSaved}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        />
      </Box>

      {/* Info */}
      <Box sx={{ p: 1.25 }}>
        {producto.marca && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}
          >
            {producto.marca}
          </Typography>
        )}
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.3,
            mb: 0.75,
          }}
        >
          {producto.nombre}
        </Typography>
        {showCodigoBarras && producto.codigo_barras && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontFamily: 'monospace',
              fontSize: '0.68rem',
              letterSpacing: 0.3,
              opacity: 0.75,
              mb: 0.5,
            }}
          >
            {producto.codigo_barras}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body1" fontWeight={800} color="primary">
            {moneda} {Number(producto.precio_venta || 0).toFixed(2)}
          </Typography>
          <CatalogStockLabel producto={producto} variant="text" />
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CatalogPublico() {
  const { slug, productId: routeProductId } = useParams();
  const navigate = useNavigate();

  // Catalog data
  const [tienda,     setTienda]     = useState(null);
  const [productos,  setProductos]  = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [total,      setTotal]      = useState(0);
  const [pagina,     setPagina]     = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [busqueda,   setBusqueda]   = useState('');
  const [busquedaRaw, setBusquedaRaw] = useState('');
  const [categoria,  setCategoria]  = useState('');

  // UI
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [shareNotice, setShareNotice] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [justSavedId, setJustSavedId] = useState(null);

  const interests = useCatalogInterests(slug);

  // Detail drawer
  const [detalleProductId, setDetalleProductId] = useState(routeProductId || null);
  const [detalleInitialData, setDetalleInitialData] = useState(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setBusqueda(busquedaRaw.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [busquedaRaw]);

  // Reset page when filters change
  useEffect(() => { setPagina(1); }, [busqueda, categoria]);

  // Fetch catalog
  useEffect(() => {
    if (!slug) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    getCatalog(slug, { pagina, limite: ITEMS_PER_PAGE, busqueda, categoria }, controller.signal)
      .then((data) => {
        if (!data?.ok) throw new Error(data?.message || 'Error al cargar catálogo');
        setTienda(data.tienda);
        setProductos(data.productos || []);
        setCategorias(data.categorias || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPaginas || 1);
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError(err.response?.data?.message || err.message || 'No se pudo cargar el catálogo.');
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [slug, pagina, busqueda, categoria]);

  // Open detail if URL contains /p/:productId on mount
  useEffect(() => {
    if (routeProductId) {
      setDetalleProductId(routeProductId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetalle = useCallback((producto) => {
    setDetalleInitialData(producto);
    setDetalleProductId(producto.id);
    navigate(`/store/${slug}/p/${producto.id}`, { replace: true });
  }, [slug, navigate]);

  const closeDetalle = useCallback(() => {
    setDetalleProductId(null);
    setDetalleInitialData(null);
    navigate(`/store/${slug}`, { replace: true });
  }, [slug, navigate]);

  const markJustSaved = useCallback((id) => {
    if (!id) return;
    setJustSavedId(id);
    setTimeout(() => setJustSavedId(null), 400);
  }, []);

  const runPostAuthFlow = useCallback(
    async (product) => {
      await interests.refreshIds();

      if (!product?.id) return;
      setModalProduct(product);
      if (interests.needsWhatsappPrompt()) {
        setWhatsappModalOpen(true);
        return;
      }
      const ok = await interests.saveAfterAuth(product);
      if (ok) markJustSaved(product.id);
      await interests.refreshIds();
      interests.clearPending();
      setModalProduct(null);
    },
    [interests, markJustSaved]
  );

  const handleInterestClick = useCallback(
    async (producto) => {
      const result = await interests.toggleInterest(producto, {
        onNeedAuth: (p) => {
          setModalProduct(p);
          setModalOpen(true);
        },
        onNeedWhatsapp: (p) => {
          setModalProduct(p);
          setWhatsappModalOpen(true);
        },
      });
      if (!result.needsAuth && !result.needsWhatsapp && interests.isSaved(producto.id)) {
        markJustSaved(producto.id);
      }
    },
    [interests, markJustSaved]
  );

  const handleAuthSuccess = useCallback(async () => {
    setModalOpen(false);
    await interests.refreshIds();
    const pending =
      modalProduct ||
      interests.getPendingProduct(productos) ||
      (detalleInitialData?.id === detalleProductId ? detalleInitialData : null) ||
      (detalleProductId ? { id: detalleProductId } : null);
    await runPostAuthFlow(pending);
  }, [interests, modalProduct, productos, detalleProductId, detalleInitialData, runPostAuthFlow]);

  const handleWhatsappSaved = useCallback(async () => {
    setWhatsappModalOpen(false);
    const ok = await interests.completePendingSave();
    if (ok) {
      const p = interests.pendingProduct || modalProduct;
      markJustSaved(p?.id);
    }
    await interests.refreshIds();
    interests.clearPending();
    setModalProduct(null);
  }, [interests, modalProduct, markJustSaved]);

  const handleShare = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const url = `${window.location.origin}/store/${slug}`;
    const result = await copyCatalogLink(url);
    setShareNotice(result.message || (result.ok ? 'Enlace copiado' : 'No se pudo copiar el enlace'));
  }, [slug]);

  const moneda = tienda?.moneda || 'BOB';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (error && !tienda) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="info" sx={{ maxWidth: 480, width: '100%' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#f7f7f7' }}>

      {/* ── HEADER ── */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: '0 1px 6px rgba(0,0,0,.06)',
        }}
      >
        <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 1.5 }}>
          {/* Store name row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <StorefrontIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            {loading && !tienda ? (
              <Skeleton width={140} height={24} />
            ) : (
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1 }}>
                {tienda?.nombre ?? slug}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto' }}>
              {tienda?.whatsapp && (
                <Button
                  component="a"
                  href={`https://wa.me/${tienda.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    bgcolor: '#25D366',
                    '&:hover': { bgcolor: '#1ebe59' },
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.5,
                    minWidth: 0,
                    fontSize: '0.78rem',
                  }}
                >
                  Contactar
                </Button>
              )}
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{ borderRadius: 2, px: 1.25, py: 0.5, minWidth: 0, fontSize: '0.78rem' }}
              >
                Compartir
              </Button>
            </Box>
          </Box>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Buscar productos…"
            fullWidth
            value={busquedaRaw}
            onChange={(e) => setBusquedaRaw(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              sx: { borderRadius: 3, bgcolor: '#f0f0f0', '& fieldset': { border: 'none' } },
            }}
            sx={{ mb: categorias.length > 0 ? 1.25 : 0 }}
          />

          {/* Category filter chips */}
          {categorias.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                overflowX: 'auto',
                pb: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              <Chip
                label="Todos"
                size="small"
                onClick={() => setCategoria('')}
                color={categoria === '' ? 'primary' : 'default'}
                variant={categoria === '' ? 'filled' : 'outlined'}
                sx={{ borderRadius: 2, flexShrink: 0 }}
              />
              {categorias.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  size="small"
                  onClick={() => setCategoria(cat === categoria ? '' : cat)}
                  color={categoria === cat ? 'primary' : 'default'}
                  variant={categoria === cat ? 'filled' : 'outlined'}
                  sx={{ borderRadius: 2, flexShrink: 0 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── MAIN CONTENT ── */}
      <Box sx={{ maxWidth: 900, mx: 'auto', px: 1.5, py: 2 }}>

        {/* Result count */}
        {!loading && tienda && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            {total} {total === 1 ? 'producto' : 'productos'}
            {categoria ? ` en "${categoria}"` : ''}
            {busqueda ? ` para "${busqueda}"` : ''}
          </Typography>
        )}

        {/* Grid */}
        {loading ? (
          <CatalogGridSkeleton count={8} />
        ) : productos.length === 0 ? (
          <Fade in>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ImageIcon sx={{ fontSize: 56, color: 'grey.300', mb: 1 }} />
              <Typography color="text.secondary">
                {busqueda || categoria ? 'No se encontraron productos con esos filtros.' : 'Este catálogo no tiene productos aún.'}
              </Typography>
              {(busqueda || categoria) && (
                <Button
                  size="small"
                  sx={{ mt: 1.5 }}
                  onClick={() => { setBusquedaRaw(''); setCategoria(''); }}
                >
                  Limpiar filtros
                </Button>
              )}
            </Box>
          </Fade>
        ) : (
          <Fade in>
            <Grid container spacing={1.5}>
              {productos.map((p) => (
                <Grid item xs={6} sm={4} md={3} key={p.id} sx={{ minWidth: 0, display: 'flex' }}>
                  <ProductCard
                    producto={p}
                    moneda={moneda}
                    onClick={openDetalle}
                    showImages={tienda?.fotosPremiumActivo !== false}
                    showCodigoBarras={tienda?.mostrarCodigoBarras === true}
                    interestActive={interests.isSaved(p.id)}
                    onInterestClick={() => handleInterestClick(p)}
                    interestJustSaved={justSavedId === p.id}
                  />
                </Grid>
              ))}
            </Grid>
          </Fade>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={pagina <= 1}
              onClick={() => setPagina((p) => p - 1)}
              sx={{ borderRadius: 2 }}
            >
              Anterior
            </Button>
            <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
              {pagina} / {totalPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={pagina >= totalPages}
              onClick={() => setPagina((p) => p + 1)}
              sx={{ borderRadius: 2 }}
            >
              Siguiente
            </Button>
          </Box>
        )}
      </Box>

      {/* ── PRODUCT DETAIL BOTTOM SHEET ── */}
      {detalleProductId && (
        <CatalogProductoDetalle
          slug={slug}
          productId={detalleProductId}
          tiendaFull={tienda}
          onClose={closeDetalle}
          initialData={detalleInitialData}
          interestActive={interests.isSaved(detalleProductId)}
          onInterestClick={() => {
            const p = productos.find((x) => x.id === detalleProductId) || detalleInitialData;
            if (p) handleInterestClick(p);
          }}
          interestJustSaved={justSavedId === detalleProductId}
        />
      )}

      <CatalogInterestModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalProduct(null);
          interests.clearPending();
        }}
        onSuccess={handleAuthSuccess}
        productPreview={modalProduct || detalleInitialData}
      />

      <CatalogWhatsAppModal
        open={whatsappModalOpen}
        onClose={() => {
          setWhatsappModalOpen(false);
        }}
        onSaved={handleWhatsappSaved}
        productPreview={modalProduct || interests.pendingProduct || detalleInitialData}
      />

      <Snackbar
        open={Boolean(shareNotice)}
        autoHideDuration={2800}
        onClose={() => setShareNotice('')}
        message={shareNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={Boolean(interests.toast)}
        autoHideDuration={2200}
        onClose={() => interests.setToast('')}
        message={interests.toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
